/**
 * External Platform API Client
 * Compatibility layer - all calls go through the new API client with logging
 *
 * All API responses follow the pattern:
 *   Success: { success: true, data: { ... } }
 *   Error:   { success: false, error: { message, code?, details? } }
 */

import apiClient from '@/services/api-client';
import { logger } from '@/services/logger.service';
import { TOKEN_KEYS, GROUP_TO_ROLE, apiFetch, PROXY_BASE } from './external-api-config';
import { API, buildPath } from './api-endpoints';
import { authStorage } from './storage';
import type {
  ExternalListing,
  ExternalListingsData,
  ExternalListingFilters,
  ExternalLoginData,
  ExternalUserData,
  ExternalGroupInfo,
  SyncResponseData,
  SyncSourceStatus,
  WhatsAppSendRequest,
  EmailSendRequest,
  MessageResponseData,
  MessageTemplate,
  CreateTemplateRequest,
  OTPSendResponse,
  OTPVerifyResponse,
  ListingStatusUpdate,
  ExternalAgent,
  AgentSyncResponse,
  AgentCognitoCreateResponse,
  AgentListResponse,
  AgentFilters,
  SystemHealthResponse,
  SystemStatusResponse,
  CostReportResponse,
} from '@/types/external-listing';

// ─── Token Management ───
// Tokens are stored in httpOnly cookies by backend

class TokenManager {
  private refreshPromise: Promise<string> | null = null;

  getAccessToken(): string | null {
    // httpOnly cookie - not accessible from JS
    return null;
  }

  getIdToken(): string | null {
    // httpOnly cookie - not accessible from JS
    return null;
  }

  getRefreshToken(): string | null {
    // httpOnly cookie - not accessible from JS
    return null;
  }

  isTokenExpired(): boolean {
    // Cannot check expiry from frontend with httpOnly cookies
    return false;
  }

  saveTokens(data: { accessToken: string; refreshToken?: string; idToken?: string; expiresIn: number }) {
    // Store access token in localStorage for API requests
    if (data.accessToken) {
      localStorage.setItem('sp_ext_access_token', data.accessToken);
      logger.debug('Access token saved to localStorage');
    }
    if (data.refreshToken) {
      localStorage.setItem('sp_ext_refresh_token', data.refreshToken);
    }
    if (data.idToken) {
      localStorage.setItem('sp_ext_id_token', data.idToken);
    }
  }

  saveUserData(user: ExternalUserData) {
    authStorage.set(TOKEN_KEYS.USER_DATA, user);
  }

  getUserData(): ExternalUserData | null {
    return authStorage.get<ExternalUserData>(TOKEN_KEYS.USER_DATA);
  }

  clearAll() {
    authStorage.remove(TOKEN_KEYS.USER_DATA);
    // Clear tokens from localStorage
    localStorage.removeItem('sp_ext_access_token');
    localStorage.removeItem('sp_ext_refresh_token');
    localStorage.removeItem('sp_ext_id_token');
    logger.info('Session cleared');
  }

  async hasSession(): Promise<boolean> {
    try {
      await apiClient.get('/auth/user');
      return true;
    } catch {
      return false;
    }
  }

  async getValidToken(): Promise<string | null> {
    // Return the stored id token so restoreCognitoSession() doesn't incorrectly
    // treat a valid session as invalid and call clearAll().
    return localStorage.getItem('sp_ext_id_token');
  }
}

export const tokenManager = new TokenManager();

// ─── Helper Functions ───

async function extJSON<T>(path: string, opts: RequestInit = {}, _requireAuth = true): Promise<T> {
  try {
    const method = opts.method || 'GET';
    let result: any;

    if (method === 'GET') {
      result = await apiClient.get<any>(path);
    } else if (method === 'POST') {
      const body = opts.body ? JSON.parse(opts.body as string) : undefined;
      result = await apiClient.post<any>(path, body);
    } else if (method === 'PUT') {
      const body = opts.body ? JSON.parse(opts.body as string) : undefined;
      result = await apiClient.put<any>(path, body);
    } else if (method === 'DELETE') {
      result = await apiClient.delete<any>(path);
    }

    // Handle error responses: { success: false, error: { message, code?, details? } }
    if (result?.success === false && result?.error) {
      const errorMsg = result.error.message || 'API returned error';
      throw new Error(errorMsg);
    }

    // Unwrap { success: true, data: T } wrapper
    if (result?.success === true && result?.data !== undefined) {
      return result.data as T;
    }

    // Fallback: Return as-is if no wrapper detected (for compatibility)
    // But log this for debugging
    logger.warn('API response without success wrapper', { path, result: typeof result });
    return result as T;
  } catch (error: any) {
    // Re-throw with enhanced error info
    const statusCode = error?.response?.status;
    const errorMsg = error?.response?.data?.error?.message || error?.response?.data?.message || error.message;

    const enhancedError = new Error(errorMsg);
    (enhancedError as any).statusCode = statusCode;
    (enhancedError as any).originalError = error;
    throw enhancedError;
  }
}

// ─── Auth API ───

export const externalAuthAPI = {
  async login(email: string, password: string): Promise<ExternalLoginData> {
    logger.info('Login attempt', { email });

    const data = await extJSON<ExternalLoginData>(
      '/auth/login',
      {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      },
      false,
    );

    const rawData = data as any;
    const accessToken = rawData.accessToken || rawData.AccessToken || '';
    const idToken = rawData.idToken || rawData.IdToken || '';
    const refreshToken = rawData.refreshToken || rawData.RefreshToken || '';
    const expiresIn = rawData.expiresIn ?? rawData.ExpiresIn ?? 3600;

    tokenManager.saveTokens({
      accessToken,
      refreshToken,
      idToken,
      expiresIn,
    });

    const embeddedUser = rawData.user;
    if (embeddedUser) {
      logger.info('Login successful with embedded user', { email, groups: embeddedUser.groups });
      const userData: ExternalUserData = {
        username: embeddedUser.username || embeddedUser.email || email,
        email: embeddedUser.email || email,
        emailVerified: embeddedUser.emailVerified ?? false,
        groups: embeddedUser.groups || [],
        attributes: embeddedUser.attributes || {},
      };
      tokenManager.saveUserData(userData);
    }

    return { ...data, accessToken, idToken, refreshToken, expiresIn, user: embeddedUser };
  },

  async register(data: {
    email: string;
    password: string;
    name: string;
    group?: string;
    phone?: string;
    role?: string;
    phoneNumber?: string;
  }): Promise<{ username: string; email: string; message: string; userConfirmed?: boolean }> {
    return extJSON(API.auth.register.path, {
      method: 'POST',
      body: JSON.stringify({
        email: data.email,
        password: data.password,
        name: data.name,
        role: data.role || data.group,
        phoneNumber: data.phoneNumber || data.phone,
      }),
    });
  },

  async registerSeller(data: {
    email: string;
    password: string;
    name: string;
    phone?: string;
  }): Promise<{ username: string; email: string; message: string; userConfirmed?: boolean }> {
    return extJSON(API.auth.registerSeller.path, {
      method: 'POST',
      body: JSON.stringify({
        email: data.email,
        password: data.password,
        name: data.name,
        phoneNumber: data.phone,
      }),
    });
  },

  async adminCreateUser(data: {
    email: string;
    password?: string;
    name: string;
    group: string;
    phone?: string;
  }): Promise<{ username: string; email: string; message: string }> {
    return extJSON('/auth/register', {
      method: 'POST',
      body: JSON.stringify({
        email: data.email,
        password: data.password,
        name: data.name,
        role: data.group,
        phoneNumber: data.phone,
      }),
    });
  },

  async adminListUsers(params?: {
    limit?: number;
    paginationToken?: string;
    filter?: string;
  }): Promise<{ users: ExternalUserData[]; paginationToken?: string; count?: number }> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.paginationToken) queryParams.append('paginationToken', params.paginationToken);
    if (params?.filter) queryParams.append('filter', params.filter);
    const qs = queryParams.toString();
    return extJSON(`/auth/users${qs ? `?${qs}` : ''}`);
  },

  async adminDeleteUser(username: string): Promise<{ message: string }> {
    return extJSON(`/admin/users/${encodeURIComponent(username)}`, { method: 'DELETE' });
  },

  async getUserById(userId: string): Promise<{ user: ExternalUserData }> {
    return extJSON(`/auth/users/${encodeURIComponent(userId)}`);
  },

  async updateProfile(data: { name?: string; phoneNumber?: string }): Promise<{ message: string; user: any }> {
    return extJSON('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteAccount(): Promise<{ message: string }> {
    return extJSON('/auth/profile', { method: 'DELETE' });
  },

  async changePassword(oldPassword: string, newPassword: string): Promise<{ message: string }> {
    return extJSON('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ oldPassword, newPassword }),
    });
  },

  async updateUserRole(userId: string, groups: string[]): Promise<{
    message: string;
    userId: string;
    groups: string[];
    previousGroups: string[];
  }> {
    return extJSON('/auth/users/role', {
      method: 'PUT',
      body: JSON.stringify({ userId, groups }),
    });
  },

  async toggleUserStatus(userId: string, enabled: boolean): Promise<{ message: string }> {
    return extJSON('/auth/users/status', {
      method: 'PUT',
      body: JSON.stringify({ userId, enabled }),
    });
  },

  async getUser(): Promise<ExternalUserData> {
    const raw: any = await extJSON<any>('/auth/user');
    const data: any = raw?.user || raw;

    const username = data.username || data.Username || data.sub || '';
    const email = data.email || data.Email || '';
    const emailVerified = data.emailVerified ?? data.email_verified ?? false;

    let groups: string[] = data.groups || data.Groups || data.UserGroups || [];
    if (!Array.isArray(groups)) groups = [];

    const rawAttrs = data.attributes || data.Attributes || data.UserAttributes || {};
    let attributes: Record<string, string> = {};
    if (Array.isArray(rawAttrs)) {
      for (const attr of rawAttrs) {
        if (attr.Name && attr.Value !== undefined) {
          attributes[attr.Name] = attr.Value;
        }
      }
    } else {
      attributes = { ...rawAttrs };
    }

    if (!attributes.name && (data.name || data.Name)) {
      attributes.name = data.name || data.Name;
    }
    if (!attributes.email) {
      attributes.email = email;
    }

    const userId = data.userId || data.sub || attributes.sub || undefined;

    return {
      username,
      email,
      emailVerified: emailVerified === true || emailVerified === 'true',
      groups,
      userId,
      attributes,
    } as ExternalUserData;
  },

  async confirmSignup(email: string, code: string): Promise<{ message: string }> {
    return extJSON('/auth/confirm', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    }, false);
  },

  async resendConfirmationCode(email: string): Promise<{ message: string }> {
    return extJSON('/auth/resend-code', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }, false);
  },

  async forgotPassword(email: string): Promise<{ message: string }> {
    return extJSON('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }, false);
  },

  async confirmForgotPassword(email: string, code: string, newPassword: string): Promise<{ message: string }> {
    return extJSON('/auth/confirm-forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email, code, newPassword }),
    }, false);
  },

  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout');
    } catch (err) {
      logger.warn('Logout error (non-critical)', { error: err });
    }
    tokenManager.clearAll();
  },

  async refresh(): Promise<{ accessToken: string; idToken?: string; expiresIn: number }> {
    return extJSON('/auth/refresh', { method: 'POST' }, false);
  },

  async getGroups(): Promise<{ groups: ExternalGroupInfo[] }> {
    return extJSON('/auth/groups', {}, false);
  },

  getUserRole(groups: string[]): string {
    for (const group of groups) {
      if (GROUP_TO_ROLE[group]) return GROUP_TO_ROLE[group];
    }
    return 'public';
  },

  async hasSession(): Promise<boolean> {
    return await tokenManager.hasSession();
  },

  getStoredUser(): ExternalUserData | null {
    return tokenManager.getUserData();
  },
};

// ─── Listings API ───

export const externalListingsAPI = {
  async getListings(filters?: ExternalListingFilters): Promise<ExternalListingsData> {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.source) params.append('source', filters.source);
      if (filters.status) params.append('status', filters.status);
      if (filters.listingStatus) params.append('status', filters.listingStatus);
      if (filters.propertyType) params.append('propertyType', filters.propertyType);
      if (filters.location) params.append('location', filters.location);
      if (filters.minPrice !== undefined) params.append('priceMin', String(filters.minPrice));
      if (filters.maxPrice !== undefined) params.append('priceMax', String(filters.maxPrice));
      if (filters.limit !== undefined) params.append('limit', String(filters.limit));
      if (filters.page !== undefined) params.append('page', String(filters.page));
      if (filters.lastKey) params.append('lastKey', filters.lastKey);
    }
    const qs = params.toString();
    return extJSON<ExternalListingsData>(`/listings${qs ? `?${qs}` : ''}`);
  },

  async getListing(id: string): Promise<{ listing: ExternalListing }> {
    return extJSON<{ listing: ExternalListing }>(`/listings/${encodeURIComponent(id)}`);
  },

  async syncListings(sources?: string[]): Promise<SyncResponseData> {
    const body: Record<string, any> = {};
    if (sources && sources.length > 0) {
      body.sources = sources;
    }
    return extJSON<SyncResponseData>('/listings/sync', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async getSyncSources(): Promise<{ sources: SyncSourceStatus[] }> {
    return extJSON('/listings/sources');
  },

  async updateListingStatus(update: ListingStatusUpdate): Promise<{ listing?: ExternalListing; message?: string }> {
    const id = update.somethingPropertyId || update.listingId || update.propertyId;
    if (id) {
      return extJSON(`/listings/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: update.status,
          listingNumber: update.listingNumber,
          listingUrl: update.listingUrl,
          message: update.message,
        }),
      });
    }
    return extJSON('/listings/update-status', {
      method: 'POST',
      body: JSON.stringify(update),
    });
  },
};

// ─── Messaging API ───

export const externalMessagingAPI = {
  async sendWhatsApp(request: WhatsAppSendRequest): Promise<MessageResponseData> {
    return extJSON<MessageResponseData>('/messaging/whatsapp/send', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  async sendEmail(request: EmailSendRequest): Promise<MessageResponseData> {
    return extJSON<MessageResponseData>('/messaging/email/send', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  async getTemplates(): Promise<{ templates: MessageTemplate[] }> {
    return extJSON('/messaging/templates');
  },

  async createTemplate(request: CreateTemplateRequest): Promise<{ templateId: string; message: string }> {
    return extJSON('/messaging/templates', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  async sendWelcomeEmail(data: {
    email: string;
    name: string;
    tempPassword?: string;
  }): Promise<{ message: string; messageId: string; recipient: string }> {
    return extJSON('/messaging/send-welcome-email', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async sendPasswordResetEmail(data: {
    email: string;
    name: string;
    resetToken: string;
  }): Promise<{ message: string; messageId: string; recipient: string }> {
    return extJSON('/messaging/send-password-reset-email', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// ─── OTP API ───

export const externalOTPAPI = {
  async sendOTP(email: string): Promise<OTPSendResponse> {
    return extJSON<OTPSendResponse>(API.otp.send.path, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }, false);
  },

  async verifyOTP(email: string, code: string): Promise<OTPVerifyResponse> {
    return extJSON<OTPVerifyResponse>(API.otp.verify.path, {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    }, false);
  },
};

// ─── Agent Management API ───

export const externalAgentsAPI = {
  async syncAgents(forceFullSync = false): Promise<AgentSyncResponse> {
    return extJSON<AgentSyncResponse>('/agents/sync', {
      method: 'POST',
      body: JSON.stringify({ forceFullSync }),
    });
  },

  async getAgents(filters?: AgentFilters): Promise<AgentListResponse> {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.source) params.append('source', filters.source);
      if (filters.active !== undefined) params.append('active', String(filters.active));
      if (filters.cognitoSynced !== undefined) params.append('cognitoSynced', String(filters.cognitoSynced));
      if (filters.limit !== undefined) params.append('limit', String(filters.limit));
    }
    const qs = params.toString();
    return extJSON<AgentListResponse>(`/agents${qs ? `?${qs}` : ''}`);
  },

  async getAgent(agentId: string): Promise<{ agent: ExternalAgent }> {
    return extJSON<{ agent: ExternalAgent }>(`/agents/${encodeURIComponent(agentId)}`);
  },

  async createCognitoUsers(agentId?: string, forceSync = false): Promise<AgentCognitoCreateResponse> {
    const body: Record<string, any> = {};
    if (agentId) {
      body.agentId = agentId;
      body.forceSync = forceSync;
    }
    return extJSON<AgentCognitoCreateResponse>('/agents/create-cognito-users', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  async toggleAgent(agentId: string, disable: boolean): Promise<{ message: string }> {
    return extJSON<{ message: string }>(`/agents/${encodeURIComponent(agentId)}/disable`, {
      method: 'PUT',
      body: JSON.stringify({ disable }),
    });
  },

  async resetAgentPassword(
    agentId: string,
    options: { newPassword?: string; sendResetEmail?: boolean },
  ): Promise<{ message: string }> {
    return extJSON<{ message: string }>(`/agents/${encodeURIComponent(agentId)}/password/reset`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  },
};

// ─── System Reporting API ───

export const externalSystemAPI = {
  async getHealth(detailed = true): Promise<SystemHealthResponse> {
    return extJSON<SystemHealthResponse>(`/system/health${detailed ? '?detailed=true' : ''}`);
  },

  async getSystemStatus(): Promise<SystemStatusResponse> {
    return extJSON<SystemStatusResponse>('/system/status');
  },

  async getCostReport(period: string = 'current_month'): Promise<CostReportResponse> {
    const raw: any = await extJSON<any>(`/system/cost-report?period=${encodeURIComponent(period)}`);
    const d = raw?.data || raw;
    const monthData = period === 'last_month'
      ? (d.previousMonth || d.currentMonth)
      : (d.currentMonth || d.previousMonth);
    return {
      period,
      startDate: monthData?.start || '',
      endDate: monthData?.end || '',
      totalCost: parseFloat(monthData?.totalCost || '0'),
      currency: monthData?.unit || 'USD',
      serviceBreakdown: [],
      topServices: [],
    };
  },
};

// ─── Health Check ───

export async function checkExternalAPIHealth(): Promise<{
  status: 'connected' | 'not_configured' | 'unreachable' | 'error';
  message?: string;
}> {
  try {
    const result = await apiClient.get<any>('/system/health');
    return { status: 'connected', message: result?.message || 'Healthy' };
  } catch (err) {
    return { status: 'unreachable', message: String(err) };
  }
}
